import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Play, Clock, CheckCircle, Award, X, Search, RotateCcw } from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useAuth } from '../contexts/AuthContext';
import { graphqlService } from '../services/graphqlService';

const Dashboard = () => {
  const { modules, getModulesForUser } = useModules();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [userProgress, setUserProgress] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [prescreeningData, setPrescreeningData] = useState(null);
  const [selectedCategoryType, setSelectedCategoryType] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [assignedModules, setAssignedModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const shouldSkipPrescreeningRef = useRef(false);

  // Check for clearFilters parameter on mount and URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('clearFilters') === 'true') {
      // Set flag to skip applying prescreening filters permanently for this session
      shouldSkipPrescreeningRef.current = true;
      
      // Clear all filters immediately
      setSearchQuery('');
      setSelectedCategoryType('All');
      setSelectedLevel('All');
      setSelectedTags([]);
      setPrescreeningData(null);
      
      // Remove the query parameter from URL after a brief delay to allow other useEffects to read it
      const timer = setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.search, navigate]);

  // Check for clearFilters parameter and clear all filters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clearFilters = params.get('clearFilters');

    if (clearFilters === 'true') {
      // Clear all filters
      setSearchQuery('');
      setSelectedCategoryType('All');
      setSelectedLevel('All');
      setSelectedTags([]);
      setOpenDropdown(null);
      setPrescreeningData(null);
      
      // Clear prescreening data from localStorage to prevent reapplication
      localStorage.removeItem('truMeritPrescreening');
      
      // Remove the parameter from URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  useEffect(() => {
    if (!user || !user.email) {
      setAssignedModules([]);
      setLoadingModules(false);
      return;
    }

    // Check if we should skip applying prescreening filters (when clearFilters is set)
    // Store the applyFilters flag value BEFORE the async function to avoid race conditions
    // This ensures we have the value even if the URL parameter is removed during async operations
    const searchParams = new URLSearchParams(location.search);
    const shouldClearFilters = searchParams.get('clearFilters') === 'true';
    const shouldApplyFilters = searchParams.get('applyFilters') === 'true';
    const shouldSkipPrescreening = shouldClearFilters || shouldSkipPrescreeningRef.current;

    const loadData = async () => {
      try {
        // Load active tags from database
        const activeTags = await graphqlService.getActiveTags();
        setAvailableTags(activeTags || []);

        // Load user progress from database
        const progress = await graphqlService.getProgress(user.email);
        setUserProgress(progress || {});

        // Load prescreening data from database
        const prescreening = await graphqlService.getPrescreening(user.email);
        setPrescreeningData(prescreening);
        
        // Apply prescreening filters if applyFilters flag is set and prescreening data exists
        // Use the stored shouldApplyFilters value to avoid race conditions with URL cleanup
        if (shouldApplyFilters && prescreening && !shouldSkipPrescreening) {
          console.log('Dashboard - Applying prescreening filters:', prescreening);
          
          // Apply category filter
          if (prescreening.category) {
            setSelectedCategoryType(prescreening.category);
            console.log('Dashboard - Applied category filter:', prescreening.category);
          }
          
          // Apply level filter
          if (prescreening.level) {
            setSelectedLevel(prescreening.level);
            console.log('Dashboard - Applied level filter:', prescreening.level);
          }
          
          // Apply tags filter
          if (prescreening.tags && Array.isArray(prescreening.tags) && prescreening.tags.length > 0) {
            setSelectedTags(prescreening.tags);
            console.log('Dashboard - Applied tags filter:', prescreening.tags);
          }
        }
        // Note: If applyFilters is not set, filters remain at default values ('All' / empty)
        // This ensures all modules are visible by default unless explicitly filtered

        // Load assigned modules
        setLoadingModules(true);
        const userModules = await getModulesForUser(user);
        console.log('Dashboard - Loaded modules for user:', {
          userEmail: user.email,
          userRole: user.role,
          modulesCount: userModules?.length || 0,
          modules: userModules
        });
        setAssignedModules(Array.isArray(userModules) ? userModules : []);
        setLoadingModules(false);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setAssignedModules([]);
        setLoadingModules(false);
      }
    };

    loadData();
  }, [user, getModulesForUser, location.search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.filter-badge-wrapper')) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdown]);

  // Get filter options - always show all available options
  const categoryTypes = ['All', 'General', 'TruMerit'];
  
  // Always show all levels, not just from prescreening
  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced'];
  
  // Load tags dynamically from database
  const tags = ['All', ...availableTags.map(tag => tag.name)];

  // Helper function to check if any filters are active
  const hasActiveFilters = () => {
    return searchQuery !== '' || 
           selectedCategoryType !== 'All' || 
           selectedLevel !== 'All' || 
           selectedTags.length > 0;
  };

  // Filter modules based on selected filters and search query
  const filteredModules = assignedModules.filter(module => {
    // Search filter: match against title, description, and category
    const searchLower = searchQuery.toLowerCase().trim();
    const searchMatch = searchLower === '' || 
      module.title.toLowerCase().includes(searchLower) ||
      module.description.toLowerCase().includes(searchLower) ||
      (module.category && module.category.toLowerCase().includes(searchLower));
    
    // Category Type filter - for now, show all modules regardless of category type selection
    // This can be enhanced later to filter based on module metadata
    const categoryTypeMatch = true; // Always match for now, can be customized based on module tags/metadata
    
    // Level filter - map to module difficulty
    const levelMatch = selectedLevel === 'All' || 
      module.difficulty === selectedLevel;
    
    // Tag filter - check if module has any of the selected tags
    const tagsToCheck = selectedTags.length > 0 ? selectedTags : [];
    const moduleTags = module.tags || [];
    
    const tagMatch = tagsToCheck.length === 0 ||
      tagsToCheck.some(tag => 
        moduleTags.includes(tag) ||
        // Fallback: also check title/description for backward compatibility
        module.title.toUpperCase().includes(tag.toUpperCase()) ||
        module.description.toUpperCase().includes(tag.toUpperCase()) ||
        (module.category && module.category.toUpperCase().includes(tag.toUpperCase()))
      );
    
    const matches = searchMatch && categoryTypeMatch && levelMatch && tagMatch;
    
    // Debug logging for first module to understand filtering
    if (assignedModules.indexOf(module) === 0) {
      console.log('Dashboard - Filter check for first module:', {
        moduleTitle: module.title,
        searchQuery,
        selectedLevel,
        selectedTags,
        moduleTags,
        searchMatch,
        levelMatch,
        tagMatch,
        categoryTypeMatch,
        finalMatch: matches
      });
    }
    
    return matches;
  });
  
  console.log('Dashboard - Filtering results:', {
    totalAssignedModules: assignedModules.length,
    filteredModulesCount: filteredModules.length,
    searchQuery,
    selectedLevel,
    selectedTags,
    filtersActive: hasActiveFilters()
  });

  const getModuleProgress = (moduleId) => {
    return userProgress[moduleId] || { completed: false, quizCompleted: false, score: 0, timeSpent: 0 };
  };

  const clearAllFilters = () => {
    // Clear all filter states
    setSearchQuery('');
    setSelectedCategoryType('All');
    setSelectedLevel('All');
    setSelectedTags([]);
    setOpenDropdown(null);
    setPrescreeningData(null);
    
    // Clear prescreening data from localStorage to prevent reapplication
    localStorage.removeItem('truMeritPrescreening');
  };

  return (
    <div className="container">
      <div className="dashboard-header">
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'black' }}>Welcome to Dashboard</h1>
        <p className="text-base text-gray-600 mb-4">
          Enhance your skills with our comprehensive TruMerit training modules
        </p>
      </div>

      <div className="modules-section">
        <div className="modules-header">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#0477aa' }}>Training Modules</h2>
          
          {/* Search Bar */}
          <div className="module-search-container">
            <div className="search-bar-wrapper">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                className="module-search-input"
                placeholder="Search training modules by title, description, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
          
          <div className="module-filters-readonly">
            <div className="filter-badge-wrapper">
              <div 
                className="filter-badge"
                onClick={() => setOpenDropdown(openDropdown === 'category' ? null : 'category')}
              >
                <span className="filter-badge-label">Category Types:</span>
                <span className="filter-badge-value">{selectedCategoryType} ▼</span>
              </div>
              {openDropdown === 'category' && (
                <div className="filter-dropdown">
                  {categoryTypes.map(categoryType => (
                    <div
                      key={categoryType}
                      className={`filter-dropdown-item ${selectedCategoryType === categoryType ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCategoryType(categoryType);
                        setOpenDropdown(null);
                      }}
                    >
                      {categoryType}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-badge-wrapper">
              <div 
                className="filter-badge"
                onClick={() => setOpenDropdown(openDropdown === 'level' ? null : 'level')}
              >
                <span className="filter-badge-label">Level:</span>
                <span className="filter-badge-value">{selectedLevel} ▼</span>
              </div>
              {openDropdown === 'level' && (
                <div className="filter-dropdown">
                  {levels.map(level => (
                    <div
                      key={level}
                      className={`filter-dropdown-item ${selectedLevel === level ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedLevel(level);
                        setOpenDropdown(null);
                      }}
                    >
                      {level}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-badge-wrapper">
              <div 
                className="filter-badge"
                onClick={() => setOpenDropdown(openDropdown === 'tag' ? null : 'tag')}
              >
                <span className="filter-badge-label">Tags:</span>
                <span className="filter-badge-value">
                  {selectedTags.length > 0 ? selectedTags.join(', ') : 'All'} ▼
                </span>
              </div>
              {openDropdown === 'tag' && (
                <div className="filter-dropdown filter-dropdown-multiselect">
                  {availableTags.map(tag => {
                    const tagName = tag.name;
                    return (
                      <div
                        key={tag._id || tagName}
                        className={`filter-dropdown-item filter-dropdown-item-checkbox ${selectedTags.includes(tagName) ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedTags.includes(tagName)) {
                            setSelectedTags(selectedTags.filter(t => t !== tagName));
                          } else {
                            setSelectedTags([...selectedTags, tagName]);
                          }
                        }}
                      >
                        <span className={`checkbox ${selectedTags.includes(tagName) ? 'checked' : ''}`}>
                          {selectedTags.includes(tagName) && '✓'}
                        </span>
                        {tag.displayName || tagName}
                      </div>
                    );
                  })}
                  {selectedTags.length > 0 && (
                    <div 
                      className="filter-dropdown-item filter-dropdown-clear"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTags([]);
                      }}
                    >
                      Clear All
                    </div>
                  )}
                </div>
              )}
            </div>

            {hasActiveFilters() && (
              <button
                className="clear-filters-btn"
                onClick={clearAllFilters}
                title="Clear all filters"
              >
                <RotateCcw size={16} style={{ marginRight: '6px' }} />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-4">
          {filteredModules.length > 0 ? filteredModules.map((module) => {
            const progress = getModuleProgress(module.id);
            const isModuleCompleted = progress.completed;
            const isQuizCompleted = progress.quizCompleted;
            const isFullyCompleted = isModuleCompleted && isQuizCompleted;
            const isInProgress = isModuleCompleted && !isQuizCompleted;
            
            return (
              <div key={module.id} className="card module-card">
                <div className="module-header">
                  <div className="module-icon">
                    {React.isValidElement(module.icon) ? module.icon : null}
                  </div>
                  <h3 className="module-card-title">{module.title}</h3>
                </div>
                
                <p className="module-card-description">{module.description}</p>
                
                {module.tags && module.tags.length > 0 && (
                  <div className="module-tags">
                    {module.tags.map(tag => (
                      <span key={tag} className="module-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="module-stats">
                  <div className="stat">
                    <Clock size={14} />
                    <span>{module.duration} min</span>
                  </div>
                  <div className="stat">
                    <Award size={14} />
                    <span>{module.difficulty}</span>
                  </div>
                </div>

                <div className="module-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${isFullyCompleted ? 100 : isInProgress ? 50 : 0}%` }}
                    ></div>
                  </div>
                  <p className="module-progress-text">
                    {isFullyCompleted ? 'Completed' : isInProgress ? 'In Progress' : 'Not Started'}
                  </p>
                </div>

                <div className="module-actions">
                  <Link 
                    to={`/module/${module.id}`} 
                    className="btn btn-primary btn-sm"
                  >
                    <Play size={14} style={{ marginRight: '4px' }} />
                    {isModuleCompleted ? 'Review' : 'Start'}
                  </Link>
                  {isModuleCompleted && (
                    <Link 
                      to={`/quiz/${module.id}`} 
                      className="btn btn-secondary btn-sm"
                    >
                      {isQuizCompleted ? 'Retake Quiz' : 'Take Quiz'}
                    </Link>
                  )}
                </div>
              </div>
            );
          }) : (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-500 mb-4">
                <Award size={48} className="mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {assignedModules.length === 0 
                    ? 'No Modules Assigned' 
                    : searchQuery
                    ? 'No modules found matching your search'
                    : 'No Modules Match Your Filters'}
                </h3>
                <p>
                  {assignedModules.length === 0
                    ? 'You don\'t have any training modules assigned to you yet. Contact your administrator to get started.'
                    : searchQuery
                    ? 'Try a different search term or clear your search to see all modules.'
                    : 'Try adjusting your filters to see more modules.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
